import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { roomService } from '../services/room.service';
import { Room, PutRoom } from '../../interfaces/rooms';
import { Envelope } from '../../interfaces/envelope';
import { Zone } from '../../interfaces/zones';

interface RoomState {
	rooms: {
		[id: number]: Room;
	};
	roomZones: {
		[id: number]: Zone[];
	};
	selectedRoom: Room | undefined;
	isLoading: boolean;
}

const initialState = {
	rooms: [],
	roomZones: [],
	selectedRoom: undefined,
	isLoading: false,
} as RoomState;

export const fetchRooms = createAsyncThunk<
	Room[],
	void,
	{
		rejectValue: Envelope<[]> | unknown;
	}
>('room/fetchRooms', async (_undefined, { rejectWithValue }) => {
	try {
		return await roomService.fetchRooms();
	} catch (error) {
		return rejectWithValue(error);
	}
});

export const editRoom = createAsyncThunk<
	Room,
	{ id: number; params: PutRoom },
	{
		rejectValue: Envelope<[]> | unknown;
	}
>('room/editRoom', async ({ id, params }, { rejectWithValue }) => {
	try {
		return await roomService.editRooms(id, params);
	} catch (error) {
		return rejectWithValue(error);
	}
});

const roomSlice = createSlice({
	name: 'room',
	initialState,
	reducers: {
		setRoomZones: (state, action: PayloadAction<Zone[]>) => {
			const ret: { [id: number]: Zone[] } = {};
			state.roomZones = Object.values(state.rooms).reduce((obj, room) => {
				obj[room.id] = action.payload.filter((zone) => zone.fk_room === room.id);
				return obj;
			}, ret);
		},
		selectRoom: (state, action: PayloadAction<number>) => {
			state.selectedRoom = state.rooms[action.payload];
		},
		deSelectRoom: (state) => {
			state.selectedRoom = undefined;
		},
		resetRoom: (state) => {
			Object.assign(state, initialState);
		},
	},
	extraReducers: (builder) => {
		builder.addCase(fetchRooms.fulfilled, (state, action) => {
			const ret: { [id: number]: Room } = {};
			state.rooms = action.payload.reduce((obj, room) => {
				obj[room.id] = room;
				return obj;
			}, ret);
			state.isLoading = false;
		});
		builder.addCase(fetchRooms.pending, (state) => {
			state.isLoading = true;
		});
		builder.addCase(fetchRooms.rejected, (state, action) => {
			state.rooms = [];
			state.isLoading = false;
		});
		builder.addCase(editRoom.fulfilled, (state, action) => {
			state.rooms[action.payload.id] = action.payload;
			state.isLoading = false;
		});
		builder.addCase(editRoom.pending, (state) => {
			state.isLoading = true;
		});
		builder.addCase(editRoom.rejected, (state, action) => {
			state.isLoading = false;
		});
	},
});

export const { setRoomZones, selectRoom, deSelectRoom, resetRoom } = roomSlice.actions;

export default roomSlice;
